import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { HardHat, Calendar, Clock, AlertTriangle, Check, X, Edit2, Briefcase, UserX } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { calculerJoursOuvres } from '@/lib/heures-utils';
import WeekendConfirmDialog, { isWeekend } from '@/components/ui/WeekendConfirmDialog';
import { supabase } from '@/integrations/supabase/client';
import { AbsenceConflict, getTypeAbsenceLabel } from '@/types/conflits-absence';
import { useModalProtection } from '@/hooks/useModalProtection';

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste?: string;
  taux_horaire?: number;
}

interface Chantier {
  id: string;
  nom_chantier: string;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
}

interface Affectation {
  id: string;
  chantier_id: string;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number | null;
  chantiers?: { nom_chantier: string } | null;
}

interface AffectationData {
  chantierId: string;
  dateDebut: string;
  dateFin: string;
  heuresParJour: number;
}

interface HeuresModifiees {
  affectationId: string;
  heuresAujourdhui: number;
}

interface ModalAffectationChantierProps {
  isOpen: boolean;
  onClose: () => void;
  membre: Membre | null;
  chantiersDisponibles: Chantier[];
  affectationsExistantes: Affectation[];
  onConfirm: (data: AffectationData, heuresModifiees?: HeuresModifiees[]) => Promise<void>;
}

interface Chevauchement {
  affectation: Affectation;
  heuresExistantes: number;
  dateDebutChevauchement: string;
  dateFinChevauchement: string;
}

/**
 * Valide les chevauchements et calcule le total d'heures
 * Utilise les heures modifiées si disponibles
 */
function validerChevauchements(
  nouvelle: { dateDebut: string; dateFin: string; heuresParJour: number },
  existantes: Affectation[],
  heuresModifiees: Record<string, number>
): { isValid: boolean; maxHeures: number; chevauchements: Chevauchement[] } {
  const chevauchements: Chevauchement[] = [];
  const today = new Date().toISOString().split('T')[0];

  for (const aff of existantes) {
    // Chevauchement si : debut1 <= fin2 AND debut2 <= fin1
    if (aff.date_debut <= nouvelle.dateFin && nouvelle.dateDebut <= aff.date_fin) {
      // Calculer les dates de chevauchement
      const debutChev = aff.date_debut > nouvelle.dateDebut ? aff.date_debut : nouvelle.dateDebut;
      const finChev = aff.date_fin < nouvelle.dateFin ? aff.date_fin : nouvelle.dateFin;

      // Utiliser les heures modifiées si c'est aujourd'hui, sinon les heures standard
      const heuresExistantes = heuresModifiees[aff.id] !== undefined
        ? heuresModifiees[aff.id]
        : (aff.heures_par_jour || 8);

      chevauchements.push({
        affectation: aff,
        heuresExistantes,
        dateDebutChevauchement: debutChev,
        dateFinChevauchement: finChev,
      });
    }
  }

  // Total max = heures nouvelles + somme des existantes sur chevauchement
  const totalHeures =
    nouvelle.heuresParJour + chevauchements.reduce((sum, c) => sum + c.heuresExistantes, 0);

  return {
    isValid: totalHeures <= 16,
    maxHeures: totalHeures,
    chevauchements,
  };
}

/**
 * Vérifie si aujourd'hui est dans la période de l'affectation
 */
function estAujourdhuiDansPeriode(dateDebut: string, dateFin: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  return dateDebut <= today && today <= dateFin;
}

export function ModalAffectationChantier({
  isOpen,
  onClose,
  membre,
  chantiersDisponibles,
  affectationsExistantes,
  onConfirm,
}: ModalAffectationChantierProps) {
  useModalProtection(isOpen);
  const [isLoading, setIsLoading] = useState(false);

  // États du formulaire pour la nouvelle affectation
  const [chantierId, setChantierId] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [heuresParJour, setHeuresParJour] = useState(8);
  const [joursOuvres, setJoursOuvres] = useState(0);

  // États pour les heures modifiées sur les affectations existantes (aujourd'hui uniquement)
  const [heuresModifiees, setHeuresModifiees] = useState<Record<string, number>>({});

  // État pour le popup de confirmation weekend
  const [showWeekendDialog, setShowWeekendDialog] = useState(false);
  const [pendingDateFin, setPendingDateFin] = useState<string>('');
  const [inclureWeekendFin, setInclureWeekendFin] = useState(false);

  // État pour les conflits d'absence
  const [absenceConflicts, setAbsenceConflicts] = useState<AbsenceConflict[]>([]);
  const [showAbsenceConflictModal, setShowAbsenceConflictModal] = useState(false);
  const [isCheckingAbsences, setIsCheckingAbsences] = useState(false);

  // Guard pour ne réinitialiser le formulaire QUE quand le modal s'ouvre
  // (pas quand affectationsExistantes change en arrière-plan via Realtime)
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal vient de s'ouvrir → réinitialiser le formulaire
      const today = new Date().toISOString().split('T')[0];
      setChantierId('');
      setDateDebut(today);
      setDateFin(today); // Par défaut : 1 jour de travail (aujourd'hui)
      setHeuresParJour(8);

      // Initialiser les heures modifiées avec les valeurs actuelles
      const initialHeures: Record<string, number> = {};
      affectationsExistantes.forEach((aff) => {
        if (estAujourdhuiDansPeriode(aff.date_debut, aff.date_fin)) {
          initialHeures[aff.id] = aff.heures_par_jour || 8;
        }
      });
      setHeuresModifiees(initialHeures);
      setJoursOuvres(1); // Par défaut 1 jour (aujourd'hui)
      wasOpenRef.current = true;
    }
    if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, affectationsExistantes]);

  // Calcul automatique des jours ouvrés quand les dates changent
  useEffect(() => {
    if (dateDebut && dateFin && dateDebut <= dateFin) {
      const jours = calculerJoursOuvres(dateDebut, dateFin, inclureWeekendFin);
      setJoursOuvres(jours);
    } else {
      setJoursOuvres(0);
    }
  }, [dateDebut, dateFin, inclureWeekendFin]);

  // Gérer le changement de date de fin avec vérification weekend
  const handleDateFinChange = (newDateFin: string) => {
    if (!newDateFin) {
      setDateFin('');
      return;
    }
    const date = parseISO(newDateFin);
    if (isWeekend(date)) {
      // La date tombe un weekend, demander confirmation
      setPendingDateFin(newDateFin);
      setShowWeekendDialog(true);
    } else {
      // Jour de semaine, appliquer directement
      setDateFin(newDateFin);
      setInclureWeekendFin(false);
    }
  };

  // Callback du dialogue weekend
  const handleWeekendConfirm = (inclure: boolean) => {
    setInclureWeekendFin(inclure);
    setDateFin(pendingDateFin);
    setShowWeekendDialog(false);
    setPendingDateFin('');
  };

  // Validation des chevauchements en temps réel (avec heures modifiées)
  const validation = useMemo(() => {
    if (!dateDebut || !dateFin) {
      return { isValid: true, maxHeures: heuresParJour, chevauchements: [] };
    }
    return validerChevauchements(
      { dateDebut, dateFin, heuresParJour },
      affectationsExistantes,
      heuresModifiees
    );
  }, [dateDebut, dateFin, heuresParJour, affectationsExistantes, heuresModifiees]);

  // Vérifier si le formulaire est valide
  const isFormValid = useMemo(() => {
    return (
      chantierId !== '' &&
      dateDebut !== '' &&
      dateFin !== '' &&
      dateDebut <= dateFin &&
      heuresParJour > 0 &&
      heuresParJour <= 16 &&
      validation.isValid
    );
  }, [chantierId, dateDebut, dateFin, heuresParJour, validation.isValid]);

  // Nom du chantier sélectionné (pour affichage dynamique)
  const selectedChantierNom = useMemo(() => {
    const chantier = chantiersDisponibles.find((c) => c.id === chantierId);
    return chantier?.nom_chantier || null;
  }, [chantiersDisponibles, chantierId]);

  /**
   * Vérifie si le membre a des absences déclarées sur la période d'affectation
   * Utilise la fonction SQL check_membre_absences
   */
  const checkAbsencesConflict = async (): Promise<{ hasConflict: boolean; absences: AbsenceConflict[] }> => {
    if (!membre?.id || !dateDebut || !dateFin) {
      return { hasConflict: false, absences: [] };
    }

    try {
      const { data, error } = await supabase.rpc('check_membre_absences', {
        p_membre_id: membre.id,
        p_date_debut: dateDebut,
        p_date_fin: dateFin,
      });

      if (error) {
        console.error('Erreur vérification absences:', error);
        return { hasConflict: false, absences: [] };
      }

      return {
        hasConflict: (data?.length || 0) > 0,
        absences: data || [],
      };
    } catch (err) {
      console.error('Exception vérification absences:', err);
      return { hasConflict: false, absences: [] };
    }
  };

  // Gérer le changement d'heures pour une affectation existante
  const handleHeuresChange = (affectationId: string, value: string) => {
    const heures = Math.min(16, Math.max(0, parseFloat(value) || 0));
    setHeuresModifiees((prev) => ({ ...prev, [affectationId]: heures }));
  };

  const handleConfirm = async () => {
    if (!isFormValid) return;

    setIsCheckingAbsences(true);
    try {
      // NOUVEAU : Vérifier les absences avant de créer l'affectation
      const { hasConflict, absences } = await checkAbsencesConflict();

      if (hasConflict) {
        // Afficher la modal de confirmation de conflit
        setAbsenceConflicts(absences);
        setShowAbsenceConflictModal(true);
        setIsCheckingAbsences(false);
        return;
      }

      // Pas de conflit, procéder à la création
      await proceedWithAffectation();
    } catch (err) {
      console.error('Erreur lors de la vérification:', err);
      setIsCheckingAbsences(false);
    }
  };

  /**
   * Procède à la création de l'affectation (après vérification ou confirmation)
   */
  const proceedWithAffectation = async () => {
    setIsLoading(true);
    setIsCheckingAbsences(false);
    try {
      // Préparer les heures modifiées à envoyer
      const heuresAEnvoyer: HeuresModifiees[] = Object.entries(heuresModifiees)
        .filter(([id, heures]) => {
          const aff = affectationsExistantes.find((a) => a.id === id);
          // Envoyer seulement si les heures ont changé
          return aff && heures !== (aff.heures_par_jour || 8);
        })
        .map(([id, heures]) => ({
          affectationId: id,
          heuresAujourdhui: heures,
        }));

      await onConfirm(
        {
          chantierId,
          dateDebut,
          dateFin,
          heuresParJour,
        },
        heuresAEnvoyer.length > 0 ? heuresAEnvoyer : undefined
      );
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  if (!membre) return null;

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <HardHat className="h-5 w-5" />
            Affecter à un chantier
          </DialogTitle>
          <DialogDescription>
            {membre.prenom} {membre.nom}
            {membre.poste && ` - ${membre.poste}`}
            {membre.taux_horaire && ` (${membre.taux_horaire}€/h)`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Affectations existantes AUJOURD'HUI - en premier pour visibilité */}
          {affectationsExistantes.some((aff) => estAujourdhuiDansPeriode(aff.date_debut, aff.date_fin)) && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-blue-500">
                <Clock className="h-3.5 w-3.5" />
                Heures aujourd'hui ({format(new Date(), 'dd/MM', { locale: fr })})
              </Label>
              <div className="space-y-2">
                {affectationsExistantes
                  .filter((aff) => estAujourdhuiDansPeriode(aff.date_debut, aff.date_fin))
                  .map((aff) => {
                    const nomChantier = aff.chantiers?.nom_chantier || 'Chantier inconnu';
                    const heuresActuelles = heuresModifiees[aff.id] ?? (aff.heures_par_jour || 8);

                    return (
                      <div
                        key={aff.id}
                        className="p-3 rounded-lg border border-blue-500/50 bg-blue-500/10"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{nomChantier}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={16}
                              step={0.5}
                              value={heuresActuelles}
                              onChange={(e) => handleHeuresChange(aff.id, e.target.value)}
                              className="w-16 h-7 text-center text-sm"
                            />
                            <span className="text-xs text-muted-foreground">h</span>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Standard : {aff.heures_par_jour || 8}h/jour
                        </div>
                      </div>
                    );
                  })}
              </div>
              <p className="text-xs text-muted-foreground">
                Modifiez les heures travaillées aujourd'hui sur chaque chantier
              </p>
            </div>
          )}

          {/* Séparateur si affectations existantes */}
          {affectationsExistantes.some((aff) => estAujourdhuiDansPeriode(aff.date_debut, aff.date_fin)) && (
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold">Nouvelle affectation</Label>
            </div>
          )}

          {/* Sélection du chantier */}
          <div className="space-y-2">
            <Label htmlFor="chantier">Chantier *</Label>
            <Select value={chantierId} onValueChange={setChantierId}>
              <SelectTrigger id="chantier">
                <SelectValue placeholder="Sélectionner un chantier..." />
              </SelectTrigger>
              <SelectContent>
                {chantiersDisponibles.map((chantier) => (
                  <SelectItem key={chantier.id} value={chantier.id}>
                    {chantier.nom_chantier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Période d'affectation */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateDebut">
                <Calendar className="h-3.5 w-3.5 inline mr-1" />
                Date début *
              </Label>
              <Input
                id="dateDebut"
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateFin">
                <Calendar className="h-3.5 w-3.5 inline mr-1" />
                Date fin *
              </Label>
              <Input
                id="dateFin"
                type="date"
                value={dateFin}
                onChange={(e) => handleDateFinChange(e.target.value)}
              />
            </div>
          </div>

          {/* Jours ouvrés calculés automatiquement */}
          {dateDebut && dateFin && dateDebut <= dateFin && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/30 bg-primary/5">
              <Briefcase className="h-4 w-4 text-primary" />
              <span className="text-sm">
                <strong>{joursOuvres}</strong> jour{joursOuvres > 1 ? 's' : ''} travaillé{joursOuvres > 1 ? 's' : ''}
                {selectedChantierNom && <span className="text-muted-foreground ml-1">sur {selectedChantierNom}</span>}
                <span className="text-muted-foreground ml-1">(hors week-ends)</span>
              </span>
            </div>
          )}

          {/* Heures par jour */}
          <div className="space-y-2">
            <Label htmlFor="heures">
              <Clock className="h-3.5 w-3.5 inline mr-1" />
              Heures par jour {selectedChantierNom && <span className="font-normal text-muted-foreground">sur {selectedChantierNom}</span>} *
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="heures"
                type="number"
                min={0.5}
                max={16}
                step={0.5}
                value={heuresParJour}
                onChange={(e) => setHeuresParJour(Math.min(16, Math.max(0.5, parseFloat(e.target.value) || 8)))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">h/jour (max: 16h)</span>
            </div>
          </div>

          {/* Affectations existantes avec chevauchement */}
          {affectationsExistantes.length > 0 && validation.chevauchements.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                Chevauchements détectés
              </Label>
              <div className="space-y-2">
                {validation.chevauchements.map((chev) => {
                  const nomChantier = chev.affectation.chantiers?.nom_chantier || 'Chantier inconnu';

                  return (
                    <div
                      key={chev.affectation.id}
                      className="p-3 rounded-lg border border-amber-500 bg-amber-500/10"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{nomChantier}</span>
                        <Badge variant="secondary" className="text-xs">
                          {chev.heuresExistantes}h/j
                        </Badge>
                      </div>
                      <div className="text-xs text-amber-600 mt-1 font-medium">
                        Chevauchement :{' '}
                        {format(parseISO(chev.dateDebutChevauchement), 'dd/MM', { locale: fr })} →{' '}
                        {format(parseISO(chev.dateFinChevauchement), 'dd/MM', { locale: fr })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Indicateur de validation */}
          {validation.chevauchements.length > 0 && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg border ${
                validation.isValid
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-red-500 bg-red-500/10'
              }`}
            >
              {validation.isValid ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <X className="h-4 w-4 text-red-500" />
              )}
              <span
                className={`text-sm font-medium ${
                  validation.isValid ? 'text-green-600' : 'text-red-600'
                }`}
              >
                Total heures sur chevauchement : {validation.maxHeures}h/16h
              </span>
            </div>
          )}

          {/* Erreur dates */}
          {dateDebut && dateFin && dateDebut > dateFin && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-red-500 bg-red-500/10">
              <X className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-red-600">
                La date de fin doit être après la date de début
              </span>
            </div>
          )}

          {/* Indication de vérification en cours */}
          {isCheckingAbsences && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-blue-500 bg-blue-500/10">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="text-sm text-blue-600">Vérification des absences...</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !isFormValid}>
            {isLoading ? 'Affectation en cours...' : 'Affecter au chantier'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Dialogue de confirmation weekend */}
      <WeekendConfirmDialog
        isOpen={showWeekendDialog}
        onClose={() => {
          setShowWeekendDialog(false);
          setPendingDateFin('');
        }}
        onConfirm={handleWeekendConfirm}
        dateFin={pendingDateFin ? parseISO(pendingDateFin) : new Date()}
        type="membre"
      />

      {/* Modal de conflit d'absence */}
      <Dialog open={showAbsenceConflictModal} onOpenChange={setShowAbsenceConflictModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-500">
              <UserX className="h-5 w-5" />
              Conflit d'absence détecté
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm">
              <strong>{membre?.prenom} {membre?.nom}</strong> a une absence déclarée qui chevauche
              cette période d'affectation :
            </p>

            <div className="space-y-2">
              {absenceConflicts.map((absence) => (
                <div
                  key={absence.absence_id}
                  className="p-3 rounded-lg border border-orange-500/50 bg-orange-500/10"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {getTypeAbsenceLabel(absence.type_absence)}
                    </span>
                    <Badge variant="secondary" className="bg-orange-500/20 text-orange-700">
                      {absence.jours_conflit} jour{absence.jours_conflit > 1 ? 's' : ''} impacté{absence.jours_conflit > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Du {format(parseISO(absence.date_debut), 'dd/MM/yyyy', { locale: fr })} au{' '}
                    {format(parseISO(absence.date_fin), 'dd/MM/yyyy', { locale: fr })}
                  </div>
                  {absence.motif && (
                    <div className="text-xs text-muted-foreground mt-1 italic">
                      Motif : {absence.motif}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Si vous créez cette affectation, les jours d'absence seront comptés mais le membre
                ne sera pas physiquement présent. Les métriques de coût pourraient être incorrectes.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowAbsenceConflictModal(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowAbsenceConflictModal(false);
                proceedWithAffectation();
              }}
            >
              Créer quand même
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
